import { Link } from "react-router-dom";
import { ArrowRight, Video } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { FadeUp, ParallaxImage } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/useSeo";
import { IMAGES } from "@/constants/site";

const CONTENT = {
  en: {
    eyebrow: "Services & specialisations",
    h1a: "Therapy on ",
    h1b: "human terms.",
    intro: "Your struggle does not define who you are. In our sessions, there is time, quiet, and absolute confidentiality to look together at what is weighing you down — and to find your own way forward.",
    indTitle: "Individual therapy",
    indP1: "Weekly 50-minute sessions at a regular time, for as long as the work is useful — a matter of weeks for some, longer for others. We review together openly; you are never locked in.",
    indP2: "The work is relational and post-existential: curious, honest and unhurried. We look both at what is difficult now and at the patterns underneath it, so that change can hold.",
    formatTitle: "Session format",
    formatCardTitle: "Online, via secure video",
    formatCardBody: "The same unhurried 50 minutes over a secure video call — from your home, your office, or anywhere you feel safe and unobserved. Same time each week, and just a click to join.",
    expectTitle: "What to expect",
    expect: [
      { q: "The first conversation", a: "We start with a free 15-minute call. You can say as much or as little as you like about what brings you, ask questions, and get a feel for how it might be to work together." },
      { q: "The first few sessions", a: "If we decide to go ahead, we meet weekly at a regular time. The first sessions are about getting to know you properly — your history, what's hard right now, and what you'd like to be different." },
      { q: "Ongoing work", a: "Sessions last 50 minutes. Some people come for a short season; others for longer, more open-ended work. We review together regularly, and you can pause or finish whenever you choose." },
      { q: "Between sessions", a: "Therapy works in the space between too. We agree upfront how best to stay in touch for practical matters, and what to do if you need urgent support." },
    ],
    focusTitle: "Areas of focus",
    focusIntro: "These are common starting points, not a limit. If something else is weighing on you, you're welcome to ask.",
    focusChips: ["Anxiety & overwhelm", "Low mood & depression", "Relationships", "Work & burnout", "Grief & loss", "Life transitions", "Self-esteem", "Feeling stuck"],
    feesLine: "Fees, cancellation and other practical details are answered ",
    feesLink: "here",
    ctaBtn: "Book a Consultation",
  },
  el: {
    eyebrow: "Υπηρεσίες & εξειδικεύσεις",
    h1a: "Θεραπεία, με ",
    h1b: "ανθρώπινους όρους.",
    intro: "Η δυσκολία δεν ορίζει ποιος είστε. Στις συνεδρίες μας υπάρχει χρόνος, ησυχία και απόλυτη εμπιστευτικότητα, για να δούμε μαζί τι σας βαραίνει — και να βρούμε τον δικό σας τρόπο να συνεχίσετε.",
    indTitle: "Ατομική ψυχοθεραπεία",
    indP1: "Εβδομαδιαίες συνεδρίες 50 λεπτών σε σταθερή ώρα, για όσο διάστημα η δουλειά είναι χρήσιμη — μερικές εβδομάδες για κάποιους, μεγαλύτερο διάστημα για άλλους. Επανεξετάζουμε μαζί ανοιχτά· δεν είστε ποτέ δεσμευμένοι.",
    indP2: "Η δουλειά είναι σχεσιακή και μετα-υπαρξιακή: αυθεντική, ειλικρινής και χωρίς βιασύνη. Κοιτάμε τόσο ό,τι είναι δύσκολο τώρα όσο και τα μοτίβα από πίσω του, ώστε η αλλαγή να αντέξει στο χρόνο.",
    formatTitle: "Μορφή συνεδρίας",
    formatCardTitle: "Online, μέσω ασφαλούς βιντεοκλήσης",
    formatCardBody: "Τα ίδια αβίαστα 50 λεπτά μέσω ασφαλούς βιντεοκλήσης — από το σπίτι, το γραφείο ή από οπουδήποτε νιώθετε ασφάλεια και μη παρακολούθηση. Ίδια ώρα κάθε εβδομάδα, και ένα μόνο κλικ για να μπείτε.",
    expectTitle: "Τι να περιμένετε",
    expect: [
      { q: "Η πρώτη γνωριμία", a: "Ξεκινάμε με μια δωρεάν κλήση 15 λεπτών. Μπορείτε να πείτε όσο θέλετε για ό,τι σας φέρνει, να κάνετε ερωτήσεις και να νιώσετε πώς θα είναι να δουλέψουμε μαζί." },
      { q: "Οι πρώτες συνεδρίες", a: "Αν αποφασίσουμε να προχωρήσουμε, συναντιόμαστε εβδομαδιαία σε σταθερή ώρα. Οι πρώτες συνεδρίες είναι για να γνωριστούμε σωστά — η ιστορία σας, τι είναι δύσκολο τώρα, και τι θα θέλατε να είναι διαφορετικό." },
      { q: "Η συνεχής δουλειά", a: "Οι συνεδρίες διαρκούν 50 λεπτά. Κάποιοι έρχονται για μια σύντομη περίοδο· άλλοι για μεγαλύτερο, πιο ανοιχτό διάστημα. Επανεξετάζουμε μαζί τακτικά, και μπορείτε να κάνετε παύση ή να ολοκληρώσετε όποτε επιθυμείτε." },
      { q: "Μεταξύ των συνεδριών", a: "Η θεραπεία δουλεύει και στον ενδιάμεσο χώρο. Συμφωνούμε εκ των προτέρων πώς επικοινωνούμε για πρακτικά θέματα, και τι κάνουμε αν χρειαστείτε άμεση υποστήριξη." },
    ],
    focusTitle: "Θέματα που δουλεύουμε",
    focusIntro: "Αυτά είναι συνηθισμένα σημεία εκκίνησης. Αν κάτι άλλο σας βαραίνει, είναι ευπρόσδεκτο να το ρωτήσετε.",
    focusChips: ["Άγχος & φορτισμένη καθημερινότητα", "Καταθλιπτική διάθεση", "Σχέσεις", "Δουλειά & burnout", "Πένθος & απώλεια", "Μεταβάσεις ζωής", "Αυτοεκτίμηση", "Αίσθηση αδιέξοδου"],
    feesLine: "Οι χρεώσεις, οι ακυρώσεις και οι υπόλοιπες πρακτικές λεπτομέρειες απαντώνται ",
    feesLink: "εδώ",
    ctaBtn: "Κλείστε Ραντεβού",
  },
};

const Services = () => {
  const { lang } = useLang();
  const c = CONTENT[lang];
  useSeo("services");

  return (
    <div data-testid="page-services">
      <section className="container-x pb-24 pt-32 md:pt-40 lg:pt-44">
        <FadeUp>
          <p className="eyebrow mb-6">{c.eyebrow}</p>
          <h1 className="max-w-3xl font-display text-4xl font-light leading-[1.05] tracking-tight text-sage-900 sm:text-6xl">
            {c.h1a}
            <em className="italic text-sage-600">{c.h1b}</em>
          </h1>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-muted-foreground">{c.intro}</p>
        </FadeUp>

        <FadeUp>
          <div className="mt-16 grid items-center gap-12 lg:mt-24 lg:grid-cols-2 lg:gap-20">
            <div>
              <h2 className="font-display text-3xl font-light tracking-tight text-sage-900">{c.indTitle}</h2>
              <div className="mt-6 space-y-5 leading-relaxed text-foreground/80">
                <p>{c.indP1}</p>
                <p>{c.indP2}</p>
              </div>
            </div>
            <ParallaxImage
              src={IMAGES.office}
              alt="A calm therapy office with plants and soft light"
              className="aspect-[4/3] rounded-3xl"
              distance={35}
            />
          </div>
        </FadeUp>

        <FadeUp>
          <section className="mt-24 border-t border-sage-600/10 pt-14">
            <h2 className="font-display text-3xl font-light tracking-tight text-sage-900">{c.formatTitle}</h2>
            <div className="mt-10">
              <div className="grain-card bg-card p-10">
                <Video className="h-6 w-6 text-clay" strokeWidth={1.5} />
                <h3 className="mt-5 font-display text-2xl font-light text-sage-900">{c.formatCardTitle}</h3>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">{c.formatCardBody}</p>
              </div>
            </div>
          </section>
        </FadeUp>

        <FadeUp>
          <section className="mt-24 border-t border-sage-600/10 pt-14">
            <h2 className="font-display text-3xl font-light tracking-tight text-sage-900">{c.expectTitle}</h2>
            <Accordion type="single" collapsible className="mt-8">
              {c.expect.map((item) => (
                <AccordionItem key={item.q} value={item.q} className="border-sage-600/10">
                  <AccordionTrigger
                    data-testid={`services-expect-trigger-${item.q.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="py-6 text-left font-display text-xl font-light text-sage-900 hover:no-underline"
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="max-w-2xl text-base leading-relaxed text-muted-foreground">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        </FadeUp>

        <FadeUp>
          <section className="mt-24 border-t border-sage-600/10 pt-14">
            <h2 className="font-display text-3xl font-light tracking-tight text-sage-900">{c.focusTitle}</h2>
            <p className="mt-4 max-w-2xl leading-relaxed text-muted-foreground">{c.focusIntro}</p>
            <ul className="mt-8 flex flex-wrap gap-3">
              {c.focusChips.map((f) => (
                <li
                  key={f}
                  className="rounded-full border border-sage-600/15 bg-secondary/60 px-5 py-2.5 text-sm text-sage-800"
                >
                  {f}
                </li>
              ))}
            </ul>
          </section>
        </FadeUp>

        <FadeUp>
          <div className="mt-24 flex flex-col items-start justify-between gap-6 rounded-3xl border border-sage-600/10 bg-secondary/50 p-10 sm:flex-row sm:items-center">
            <p className="max-w-md font-display text-2xl font-light leading-snug text-sage-900">
              {c.feesLine}
              <Link to="/faq" className="link-underline italic text-sage-600">{c.feesLink}</Link>.
            </p>
            <Button
              asChild
              data-testid="services-cta-book"
              className="rounded-full bg-sage-600 px-6 py-5 text-background hover:bg-sage-700"
            >
              <Link to="/intake" className="inline-flex items-center gap-2">
                {c.ctaBtn} <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </FadeUp>
      </section>
    </div>
  );
};

export default Services;
